import { parseISO } from 'date-fns';
import React from 'react';
import { TimeElement } from '../TimeElement/TimeElement';
import formatCloseRelative from '../Util/formatCloseRelative';
import type { CongestionTaxOverview, CongestionZoneOverview } from './CongestionTaxInfo.types';

interface CongestionTaxInfoProps {
    overview?: CongestionTaxOverview;
    region?: string;
    onContentLoad?(): void;
}

interface CongestionTaxInfoState {
    overview: CongestionTaxOverview;
    loaded: boolean;
}

export class CongestionTaxInfo extends React.Component<CongestionTaxInfoProps, CongestionTaxInfoState> {
    _url: string;
    _isMounted: boolean;
    _timer: ReturnType<typeof setTimeout>;

    constructor(props: CongestionTaxInfoProps) {
        super(props);

        this._url = `/api/congestiontax?region=${props.region}`;

        this.state = {
            overview: this.props.overview,
            loaded: !!this.props.overview,
        };
    }

    componentDidMount(): void {
        if (!this.props.overview) {
            this.update();
        } else {
            this._scheduleUpdate();
        }

        document.addEventListener('visibilitychange', this._handleVisibilityChange);
    }

    componentWillUnmount(): void {
        document.removeEventListener('visibilitychange', this._handleVisibilityChange);
        this._stopUpdates();
    }

    _scheduleUpdate() {
        if (!this._timer) {
            this._timer = setTimeout(() => {
                this._timer = null;
                this.update();
            }, 60000);
        }
    }

    _stopUpdates() {
        clearTimeout(this._timer);
        this._timer = null;
    }

    _handleVisibilityChange = () => {
        if (document.hidden) {
            this._stopUpdates();
        } else {
            this.update();
        }
    };

    async update(): Promise<void> {
        try {
            const response = await fetch(this._url);
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const overview = await response.json();
            this.setState(
                {
                    loaded: true,
                    overview,
                },
                this.props.onContentLoad,
            );
        } catch {
            this.setState(
                {
                    loaded: true,
                },
                this.props.onContentLoad,
            );
        }
        this._scheduleUpdate();
    }

    renderZone(zoneOverview: CongestionZoneOverview, index: number, zones: CongestionZoneOverview[]): React.ReactElement {
        const nextTaxStartTimeISO = parseISO(zoneOverview.nextTax.startTime);
        return (
            <div key={zoneOverview.id}>
                {zones.length > 1 ? `${zoneOverview.name}, just nu ` : 'Just nu '}
                {zoneOverview.currentTax?.taxRate ?? 0} kr. (från{' '}
                <TimeElement date={nextTaxStartTimeISO}>{formatCloseRelative(nextTaxStartTimeISO, 'includeTime').replace('idag ', '')}</TimeElement>{' '}
                {zoneOverview.nextTax.taxRate} kr).
            </div>
        );
    }

    render(): React.ReactElement {
        return (
            <div>
                {this.state.loaded &&
                    (this.state.overview ? this.state.overview.zones.map(this.renderZone) : 'Information om trängselskatt kunde inte hämtas.')}
            </div>
        );
    }
}
